import React from 'react';
import { Box, Chip, Typography, Button } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { appColors } from '../../Config/Theme';
import { AlphaRange } from '../../Utils/alphaRangeHelpers';
import { SOURCE_FILTER_LABELS } from '../../Utils/personHelpers';
import type { SourceFilter } from '../../Utils/personHelpers';
import { PersonType } from '../../Types/enums';
import { PersonStatus } from '../../Types/Incident';
import { SortMode, SORT_OPTIONS } from '../../Utils/headcountFilters';
import { UI_STATUS_LABELS } from '../../Utils/headcountStatus';
import { formatTenantCompanyChipLabel } from '../../Utils/tenantCompanyHelpers';

const PERSON_TYPE_LABELS: Record<string, string> = {
  [PersonType.Employee]: 'Employees',
  [PersonType.Contractor]: 'Contractors',
  [PersonType.Tenant]: 'Tenants',
  [PersonType.Visitor]: 'Visitors',
  [PersonType.Unlisted]: 'Manual Add',
};

interface HeadcountActiveFiltersProps {
  filteredCount: number;
  sortMode: SortMode;
  statusFilter?: PersonStatus | 'all';
  alphaFilter: AlphaRange;
  personTypeFilters: string[];
  rosterContextFilters: SourceFilter[];
  /** Label describing the active roster view filter (e.g. "Practice: Information Technology") */
  viewFilterLabel?: string;
  onRemoveAlpha: () => void;
  onRemovePersonType: (value: string) => void;
  onRemoveRosterContext: (value: SourceFilter) => void;
  onClearAll: () => void;
  tenantCompanyFilters: string[];
  /** Map from company key to display label */
  tenantCompanyLabels: Record<string, string>;
  onRemoveTenantCompany: (value: string) => void;
}

const HeadcountActiveFilters: React.FC<HeadcountActiveFiltersProps> = ({
  filteredCount,
  sortMode,
  statusFilter = 'all',
  alphaFilter,
  personTypeFilters,
  rosterContextFilters,
  viewFilterLabel,
  onRemoveAlpha,
  onRemovePersonType,
  onRemoveRosterContext,
  onClearAll,
  tenantCompanyFilters,
  tenantCompanyLabels,
  onRemoveTenantCompany,
}) => {
  const hasAdvancedFilters =
    personTypeFilters.length > 0 || rosterContextFilters.length > 0 || tenantCompanyFilters.length > 0;

  const sortLabel = SORT_OPTIONS.find((o) => o.value === sortMode)?.label ?? 'Last Name Ascending';

  // Build status context for summary
  const statusLabel = statusFilter !== 'all' ? UI_STATUS_LABELS[statusFilter] : undefined;
  const personWord = filteredCount === 1 ? 'person' : 'people';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minHeight: hasAdvancedFilters ? 36 : 24 }}>
      {/* Summary text */}
      <Typography variant="caption" sx={{ color: appColors.text.caption, fontWeight: 500, whiteSpace: 'nowrap' }}>
        Showing {filteredCount} {statusLabel ? `${statusLabel} ` : ''}{personWord}
        {viewFilterLabel && ` \u00B7 ${viewFilterLabel}`}
        {alphaFilter !== 'All' && ` \u00B7 Last names ${alphaFilter}`}
        {' \u00B7 '}{sortLabel}
      </Typography>

      {/* Active filter chips */}
      {hasAdvancedFilters && (
        <>
          <Typography variant="caption" sx={{ color: appColors.text.caption, fontWeight: 600, ml: 0.5 }}>
            {'\u00B7'} Filtered by:
          </Typography>

          {personTypeFilters.map((pt) => (
            <Chip
              key={pt}
              size="small"
              label={PERSON_TYPE_LABELS[pt] ?? pt}
              onDelete={() => onRemovePersonType(pt)}
              deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              aria-label={`Remove ${PERSON_TYPE_LABELS[pt] ?? pt} filter`}
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                bgcolor: `${appColors.primary.main}14`,
                color: appColors.primary.main,
                border: `1px solid ${appColors.primary.main}40`,
                '& .MuiChip-deleteIcon': { color: appColors.primary.main },
              }}
            />
          ))}

          {rosterContextFilters.map((rc) => (
            <Chip
              key={rc}
              size="small"
              label={SOURCE_FILTER_LABELS[rc] ?? rc}
              onDelete={() => onRemoveRosterContext(rc)}
              deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              aria-label={`Remove ${SOURCE_FILTER_LABELS[rc] ?? rc} filter`}
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                bgcolor: `${appColors.primary.main}14`,
                color: appColors.primary.main,
                border: `1px solid ${appColors.primary.main}40`,
                '& .MuiChip-deleteIcon': { color: appColors.primary.main },
              }}
            />
          ))}

          {tenantCompanyFilters.map((tc) => {
            const chipLabel = formatTenantCompanyChipLabel(tenantCompanyLabels[tc] ?? tc);
            return (
              <Chip
                key={`tc-${tc}`}
                size="small"
                label={chipLabel}
                onDelete={() => onRemoveTenantCompany(tc)}
                deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                aria-label={`Remove ${chipLabel} filter`}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  bgcolor: `${appColors.primary.main}14`,
                  color: appColors.primary.main,
                  border: `1px solid ${appColors.primary.main}40`,
                  '& .MuiChip-deleteIcon': { color: appColors.primary.main },
                }}
              />
            );
          })}

          <Button
            size="small"
            onClick={onClearAll}
            sx={{ fontSize: '0.75rem', fontWeight: 600, minHeight: 28, textTransform: 'none', color: appColors.text.caption }}
          >
            Clear all
          </Button>
        </>
      )}
    </Box>
  );
};

export default React.memo(HeadcountActiveFilters);
